import { createUuid } from '@notifi/uuid/browser';
import { useEffect, useId, useMemo, useRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { ChevronDown, ChevronRight, Copy, GripVertical, Palette, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Card, CardContent } from '@/components/atoms/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import type { PickedLineItem } from '@/components/organisms/LineItemPicker';
import { FinancialsSection } from '@/components/organisms/FinancialsSection';
import { EstimateLineItemsSection } from '@/components/organisms/estimates/EstimateLineItemsSection';
import {
  computeProposalOptionPreview,
  mapProposalLineItemToEditable,
} from '@/routes/_dashboard/dashboard/proposals/_shared';
import type { EstimateMembershipInfo } from '@/hooks/estimates/useEstimateMembershipInfo';
import { combineEstimateMembershipInfo } from '@/hooks/estimates/useEstimateMembershipInfo';
import { planToEstimateMembershipInfo } from '@/hooks/estimates/useMembershipInfoWithInCart';
import type { MembershipPlan } from '@/components/organisms/pricebook/support/membership-plan-shared';
import { cn } from '@/lib/utils';
import { useEstimateLineItemState } from '@/hooks/estimates/useEstimateLineItemState';
import { useLaborRateProfiles } from '@/hooks/pricebook/useDynamicPricing';
import { documentLinePickers } from '@/components/organisms/sales/document-line-pickers';
import { EstimateTemplateLinePicker } from '@/components/organisms/proposals/EstimateTemplateLinePicker';
import { proposalOptionDepositAmountOn } from '@/components/organisms/proposals/support/proposal-option-math';
import type { EditableLineItem as EstimateEditableLineItem } from '@/routes/_dashboard/dashboard/estimates/_shared';
import { DocumentCostPanel } from '@/components/organisms/sales/DocumentCostPanel';
import type { DraftEstimatePaymentSchedule } from '@/components/organisms/payment-schedules/estimateTemplateValidation';

// ─── Tier config ─────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components
export const TIER_CONFIG = [
  { accent: '#64748b', light: '#f8fafc' },
  { accent: '#6366f1', light: '#eef2ff' },
  { accent: '#f59e0b', light: '#fffbeb' },
  { accent: '#10b981', light: '#ecfdf5' },
  { accent: '#ef4444', light: '#fff1f2' },
];

// ─── Types ───────────────────────────────────────────────────────────────────

export interface EditableLineItem extends PickedLineItem {
  /** Estimate editor row identity while the proposal is being composed. */
  _rowId?: string;
  /** Stable persisted row identity. Catalog ids may repeat within an option. */
  rowId?: string;
  /** One-save bridge for rows persisted before rowId existed. */
  legacySourceIndex?: number;
  isOptional: boolean;
  /** Service-call job types: this line is part of the visit price and is
   * credited toward the work when the customer proceeds (default). false =
   * always charged (e.g. materials consumed during the visit). */
  isServiceCallFee?: boolean;
  isSelected: boolean;
  costBasis: number;
  markupPercent: number;
  estimatedHours?: number;
  /**
   * The operator's own unit price for this line, ahead of the pricebook's and
   * of any strategy — the persisted spelling of `unitPrice` (see `unitPriceOf`
   * in lib/sales/line-economics). Absent means the line prices off `basePrice`,
   * which is what every option written so far does.
   */
  manualPriceOverride?: number | null;
  /** Per-line discounts, carried so an editor round trip does not drop them. */
  discountAmount?: number;
  discountPercent?: number;
  /**
   * The canonical, positive form of `hiddenFromCustomer`: whether this line
   * renders on the customer's document. Absent means visible.
   */
  visibleToCustomer?: boolean;
  /** Display ordering index (ascending). */
  sortOrder?: number;
  /** The labor rows behind a FLAT service's hours. Cost only — see `laborCostRate`. */
  laborCostLines?: import('@/types/pricebook').ServiceLaborLine[];
  /** Editor-only memory used while toggling taxability. */
  taxLineTypeBeforeExempt?: string | null;
}

export interface OptionState {
  clientId: string;
  /** Server-owned identity, absent until a newly created option is reloaded. */
  optionKey?: string;
  /** One-save bridge for options persisted before optionKey existed. */
  legacyOptionIndex?: number;
  label: string;
  description: string;
  color?: string;
  estimateTemplateId?: string;
  items: EditableLineItem[];
  expanded: boolean;
  recommended: boolean;
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  discountType?: 'PERCENT' | 'FLAT';
  discountValue?: number;
  discountReason?: string;
  depositType: '$' | '%';
  depositValue: number;
  depositRequired: boolean;
  paymentScheduleTemplate?: DraftEstimatePaymentSchedule | null;
  /**
   * The deposit this option was LOADED with, in the editor's own spelling.
   * The bound below is a rule about input — the server judges only a deposit
   * the request SETS and keeps an option storing an oversized one editable —
   * so the inline error needs the same baseline to stay silent on a figure the
   * save would be allowed to re-send. Absent on a new, duplicated or
   * template-seeded option: those have nothing stored, so everything is a set.
   *
   * `storedDepositRequired` is part of the baseline because the server's third
   * clause is: turning a deposit ON that the row stored OFF is a deposit this
   * request SETS, whatever its figures say. Left out, an option storing a
   * switched-off 500% deposit was "unchanged" the moment it was switched on —
   * the gate waved it through and the server 400'd, which is the opaque error
   * this rule exists to prevent. The estimate form's baseline carries it the
   * same way (`form.tsx` — `depositRequired && !savedDepositForm.depositRequired`).
   */
  storedDepositType?: '$' | '%';
  storedDepositValue?: number;
  storedDepositRequired?: boolean;
}

interface OptionCardProps {
  opt: OptionState;
  idx: number;
  totalOptions: number;
  hasRecommended: boolean;
  delta?: number;
  previousLabel?: string;
  paymentScheduleControls?: ReactNode;
  dragHandleProps?: ButtonHTMLAttributes<HTMLButtonElement>;
  isDragging?: boolean;
  canDuplicate?: boolean;
  onUpdate: (updates: Partial<OptionState>) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  // NOT-605 — active-member pricing context for the proposal's customer. When
  // set, this option's totals + per-line previews reflect member rates; null
  // (non-member) keeps list pricing.
  membershipInfo?: EstimateMembershipInfo | null;
  membershipCustomerId?: string | null;
  membershipServiceAddressId?: string | null;
  /** The parent proposal's business unit; narrows the estimate-template import. */
  businessUnitId?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

function toEstimateEditorLine(item: EditableLineItem): EstimateEditableLineItem {
  const rowId = item._rowId ?? item.rowId ?? `line-${createUuid()}`;
  return {
    ...item,
    _rowId: rowId,
    description: item.description ?? '',
    isCustom: item.isCustom ?? !item.code,
    chargeable: item.chargeable,
    pricingTreatment: item.pricingTreatment ?? null,
    hiddenFromCustomer:
      item.visibleToCustomer === false ? true : (item.hiddenFromCustomer ?? false),
    discountAmount: item.discountAmount ?? 0,
    discountPercent: item.discountPercent ?? 0,
  };
}

function toProposalEditorLine(item: EstimateEditableLineItem): EditableLineItem {
  return {
    ...item,
    rowId: item._rowId,
    description: item.description ?? '',
    taxLineType: item.taxLineType ?? null,
    membershipPlanId: item.membershipPlanId ?? undefined,
    membershipBillingOptionId: item.membershipBillingOptionId ?? undefined,
    membershipCustomerChoosesBillingOption: item.membershipCustomerChoosesBillingOption === true,
    visibleToCustomer: item.hiddenFromCustomer === true ? false : undefined,
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

export function OptionCard({
  opt,
  idx,
  totalOptions,
  hasRecommended,
  delta,
  previousLabel,
  paymentScheduleControls,
  dragHandleProps,
  isDragging = false,
  canDuplicate = true,
  onUpdate,
  onDuplicate,
  onRemove,
  membershipInfo = null,
  membershipCustomerId,
  membershipServiceAddressId,
  businessUnitId,
}: OptionCardProps) {
  const customColorInputId = useId();
  const customColorInputRef = useRef<HTMLInputElement>(null);
  const initialLinesRef = useRef<EstimateEditableLineItem[] | null>(null);
  initialLinesRef.current ??= opt.items.map(toEstimateEditorLine);
  const lineState = useEstimateLineItemState(initialLinesRef.current);
  const { lineItems, setLineItems } = lineState;
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const proposalItems = useMemo(() => lineItems.map(toProposalEditorLine), [lineItems]);
  const isPullingExternalLines = useRef(false);
  const lastProcessedLineItems = useRef(lineItems);
  const lastEmittedItems = useRef<EditableLineItem[] | null>(null);
  const lastSeenPropItems = useRef(opt.items);

  // A saved proposal or an estimate-template import can replace an option's
  // lines after the card mounted. Pull that external snapshot into the estimate
  // editor, while ignoring the exact array this card just emitted itself.
  useEffect(() => {
    if (opt.items === lastSeenPropItems.current) return;
    lastSeenPropItems.current = opt.items;
    if (opt.items === lastEmittedItems.current) return;
    // React runs the mirror effect below in this same commit, before the state
    // update requested here has rendered. Suppress both that stale snapshot
    // and the following render containing the externally supplied lines: the
    // parent already owns those lines, so neither snapshot needs emitting.
    isPullingExternalLines.current = true;
    setLineItems(opt.items.map(toEstimateEditorLine));
  }, [opt.items, setLineItems]);

  // The estimate hook is the source of truth for line edits. Mirror its complete
  // snapshot into the option so proposal totals and the eventual payload read
  // the exact same fields as the editor that produced them.
  useEffect(() => {
    // The parent already owns the initial snapshot. Comparing the actual state
    // identity (instead of a did-mount flag) is important in React Strict Mode,
    // where mount effects intentionally run twice with refs preserved.
    if (lineItems === lastProcessedLineItems.current) return;
    lastProcessedLineItems.current = lineItems;
    if (isPullingExternalLines.current) {
      isPullingExternalLines.current = false;
      return;
    }
    lastEmittedItems.current = proposalItems;
    lastSeenPropItems.current = proposalItems;
    onUpdateRef.current({ items: proposalItems });
  }, [lineItems, proposalItems]);

  const { data: laborRateProfiles = [] } = useLaborRateProfiles();

  function addPickedLineItem(item: PickedLineItem) {
    if (String(item.type).toUpperCase() !== 'MEMBERSHIP') {
      lineState.addPickedLineItem(item);
      return;
    }
    lineState.setLineItems((current) => {
      const existingIndex = current.findIndex(
        (candidate) =>
          String(candidate.type).toUpperCase() === 'MEMBERSHIP' &&
          (candidate.membershipPlanId && item.membershipPlanId
            ? candidate.membershipPlanId === item.membershipPlanId
            : candidate.lineItemId === item.lineItemId),
      );
      if (existingIndex < 0) {
        // Preserve estimate behavior when adding another membership selection.
        return [
          ...current,
          toEstimateEditorLine(
            mapProposalLineItemToEditable(item as unknown as Record<string, unknown>),
          ),
        ];
      }
      const replacement = toEstimateEditorLine(
        mapProposalLineItemToEditable(item as unknown as Record<string, unknown>),
      );
      return current.map((candidate, index) =>
        index === existingIndex
          ? {
              ...replacement,
              _rowId: candidate._rowId,
              isOptional: candidate.isOptional,
              isSelected: candidate.isSelected,
              sortOrder: candidate.sortOrder,
              quantity: 1,
            }
          : candidate,
      );
    });
  }

  // NOT-605 — price the option through the shared engine so member rates apply
  // when the proposal's customer is an active member (membershipInfo set). For a
  // non-member the numbers are identical to the old list-price calcOptionTotals.
  const optionPreview = useMemo(
    () => computeProposalOptionPreview({ ...opt, items: proposalItems }, membershipInfo),
    [opt, proposalItems, membershipInfo],
  );
  const subtotal = optionPreview.subtotal;
  const discountAmount = optionPreview.discountAmount;
  const total = optionPreview.total;
  const accent = opt.color ?? TIER_CONFIG[idx % TIER_CONFIG.length].accent;
  // Clamped the way the server's deposit engine is, against THIS option's own
  // deposit-eligible total — a proposal carries one deposit per option, never
  // one across them, and NOT-604 keeps a membership sold inside the option out
  // of the base. Without the clamp a stored 500% deposit previewed a figure
  // five times the option, which is not what the customer's page or the PDF
  // would ever print.
  //
  // Through the shared helper, so the builder, the proposal detail, the
  // customer's page and the PDF cannot each answer "what deposit is this" with
  // their own arithmetic — the editor's own copy of it is what let the amount
  // preview here and appear nowhere else.
  const depositEligibleTotal = optionPreview.depositEligibleTotal;
  const depositAmount = proposalOptionDepositAmountOn(
    { depositType: opt.depositType === '%' ? 'PERCENT' : 'FLAT', depositValue: opt.depositValue },
    depositEligibleTotal,
  );
  // Is THIS edit setting the deposit? A stored oversized value stays saveable
  // (the server refuses only a deposit the request sets), so the inline error
  // must not fire on one nobody touched. No baseline = a new option = a set.
  // Switching a stored-OFF deposit ON is a set too, even at the stored figures:
  // that is the server's own third clause, and without it an option holding a
  // switched-off 500% deposit slipped past the moment it was switched on.
  // Switching it OFF only re-sends the stored figure, which is not judged.
  const depositChanged =
    opt.storedDepositType === undefined ||
    (opt.depositRequired && !opt.storedDepositRequired) ||
    opt.depositType !== opt.storedDepositType ||
    opt.depositValue !== opt.storedDepositValue;
  const deltaLabel =
    total > 0 && typeof delta === 'number' && previousLabel
      ? `${delta > 0 ? '+' : ''}${fmt(delta)} vs ${previousLabel}`
      : null;
  const lineItemPreviews = optionPreview.lineItemPreviews;
  const membershipEligibleRowIds = optionPreview.membershipEligibleRowIds;
  const getMembershipPotentialSavings = useMemo(
    () => (plan: MembershipPlan) => {
      const withCandidate = combineEstimateMembershipInfo([
        membershipInfo,
        planToEstimateMembershipInfo(plan),
      ]);
      const candidatePreview = computeProposalOptionPreview(
        { ...opt, items: proposalItems },
        withCandidate,
      );
      return Math.max(
        0,
        candidatePreview.membershipDiscountPreview - optionPreview.membershipDiscountPreview,
      );
    },
    [membershipInfo, opt, proposalItems, optionPreview.membershipDiscountPreview],
  );
  const pickers = documentLinePickers({
    onAdd: addPickedLineItem,
    onAddConfiguredPart: lineState.addConfiguredServicePart,
    lineItems,
    membershipCustomerId,
    membershipServiceAddressId,
    getMembershipPotentialSavings,
  });

  function importEstimateTemplate(template: import('@/types/estimateTemplate').EstimateTemplate) {
    const imported = template.lineItems.map((raw) =>
      toEstimateEditorLine(
        mapProposalLineItemToEditable(raw as unknown as Record<string, unknown>),
      ),
    );
    lineState.setLineItems(imported);
    onUpdate({
      estimateTemplateId: template.id,
      ...(template.pricingMode ? { pricingMode: template.pricingMode } : {}),
    });
    toast.success(`Loaded ${imported.length} lines from "${template.name}"`);
  }

  return (
    <Card
      className={cn(
        'overflow-hidden rounded-lg border bg-card shadow-none transition-shadow',
        isDragging && 'shadow-lg ring-1 ring-primary/20',
      )}
      style={{ borderLeftColor: accent, borderLeftWidth: 3 }}
    >
      <div className="flex items-center justify-between gap-3 border-b bg-card px-3 py-2.5 sm:px-4">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            aria-label={`Reorder ${opt.label || `Option ${idx + 1}`}`}
            className="cursor-grab rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
            {...dragHandleProps}
          >
            <GripVertical size={14} />
          </button>
          <button
            type="button"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={() => onUpdate({ expanded: !opt.expanded })}
            aria-label={opt.expanded ? 'Collapse option' : 'Expand option'}
          >
            {opt.expanded ? (
              <ChevronDown size={14} className="shrink-0" />
            ) : (
              <ChevronRight size={14} className="shrink-0" />
            )}
          </button>
          <span
            className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
            style={{ backgroundColor: accent }}
          />
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span className="truncate text-sm font-semibold text-foreground">
                {opt.label || `Option ${idx + 1}`}
              </span>
              <span className="text-xs text-muted-foreground font-normal">
                {lineItems.length} item{lineItems.length !== 1 ? 's' : ''} · {fmt(total)}
              </span>
              {deltaLabel ? (
                <span className="rounded-full bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold text-success">
                  {deltaLabel}
                </span>
              ) : null}
            </div>
          </div>
          {opt.recommended && (
            <span className="rounded-full border border-success/20 bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success">
              Recommended
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground"
            onClick={onDuplicate}
            disabled={!canDuplicate}
            title={canDuplicate ? 'Duplicate option' : 'Maximum options reached'}
          >
            <Copy size={13} />
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground"
              >
                <Palette size={13} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-2">
              <div className="flex items-center gap-1.5">
                {TIER_CONFIG.map((tier) => {
                  const selected = accent === tier.accent;
                  return (
                    <button
                      key={tier.accent}
                      type="button"
                      aria-label={`Use option color ${tier.accent}`}
                      className={`h-5 w-5 rounded-full border transition-transform hover:scale-105 ${
                        selected ? 'ring-2 ring-primary ring-offset-1' : ''
                      }`}
                      style={{
                        backgroundColor: tier.accent,
                        borderColor: tier.accent,
                      }}
                      onClick={() => onUpdate({ color: tier.accent })}
                    >
                      <span className="sr-only">{tier.accent}</span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="inline-flex h-5 items-center rounded-md border px-1.5 text-[10px] font-medium text-muted-foreground hover:bg-muted"
                  onClick={() => customColorInputRef.current?.click()}
                >
                  Custom
                </button>
                <input
                  id={customColorInputId}
                  ref={customColorInputRef}
                  type="color"
                  value={accent}
                  className="sr-only"
                  onChange={(event) => onUpdate({ color: event.target.value })}
                />
              </div>
            </PopoverContent>
          </Popover>
          {totalOptions > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={onRemove}
            >
              <Trash2 size={13} />
            </Button>
          )}
        </div>
      </div>

      {opt.expanded && (
        <CardContent className="pt-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Label</Label>
              <Input
                value={opt.label}
                onChange={(e) => onUpdate({ label: e.target.value })}
                placeholder='e.g. "Basic"'
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Input
                value={opt.description}
                onChange={(e) => onUpdate({ description: e.target.value })}
                placeholder="Brief description..."
                className="h-8 text-sm"
              />
            </div>
          </div>

          {(opt.recommended || !hasRecommended) && (
            <label className="flex items-center gap-2 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={opt.recommended}
                onChange={(e) => onUpdate({ recommended: e.target.checked })}
                className="h-3.5 w-3.5 rounded"
              />
              <span className="text-xs text-muted-foreground">Mark as recommended</span>
              {opt.recommended && (
                <span className="rounded-full border border-success/20 bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success">
                  Recommended
                </span>
              )}
            </label>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Start from an estimate template</Label>
            <EstimateTemplateLinePicker
              onSelect={importEstimateTemplate}
              businessUnitId={businessUnitId}
            />
          </div>

          <div>
            <EstimateLineItemsSection
              open
              bare
              onToggle={() => undefined}
              {...pickers}
              lineItems={lineItems}
              lineItemPreviews={lineItemPreviews}
              pricingMode={opt.pricingMode}
              estimateId={undefined}
              enableLineAttachments={false}
              emptyStateEntityLabel="proposal option"
              membershipEligibleRowIds={membershipEligibleRowIds}
              membershipInfo={membershipInfo}
              componentPicker={pickers.componentPicker}
              removeConfiguredPart={lineState.removeConfiguredServicePart}
              updateQuantity={lineState.updateQuantity}
              updateName={lineState.updateName}
              updateType={lineState.updateType}
              updateUnit={lineState.updateUnit}
              updateBasePrice={lineState.updateBasePrice}
              updateManualPriceOverride={lineState.updateManualPriceOverride}
              updatePriceStrategy={lineState.updatePriceStrategy}
              updateLaborRate={lineState.updateLaborRate}
              updateEstimatedHours={lineState.updateEstimatedHours}
              updateLaborCostLines={lineState.updateLaborCostLines}
              laborRateProfiles={laborRateProfiles}
              updateDynamicPricingConfig={lineState.updateDynamicPricingConfig}
              updateDynamicMarkupProfile={lineState.updateDynamicMarkupProfile}
              updateDescription={lineState.updateDescription}
              updateEquipmentAssociation={lineState.updateEquipmentAssociation}
              updateComponentDisplayMode={lineState.updateComponentDisplayMode}
              customerId={membershipCustomerId}
              serviceAddressId={membershipServiceAddressId}
              updateMarkupPercent={lineState.updateMarkupPercent}
              updateCostBasis={lineState.updateCostBasis}
              updateTaxable={lineState.updateTaxable}
              updateTaxAccounting={lineState.updateTaxAccounting}
              updateMemberPricing={lineState.updateMemberPricing}
              onSaveLineToPricebook={lineState.linkLineToCatalogItem}
              updateChargeable={lineState.updateChargeable}
              updateHiddenFromCustomer={lineState.updateHiddenFromCustomer}
              toggleOptional={lineState.toggleOptional}
              toggleSelected={lineState.toggleSelected}
              removeLineItem={lineState.removeLineItem}
              onReorder={lineState.reorderLineItems}
            />
          </div>

          {lineItems.length > 0 && (
            <div className="space-y-4">
              {/* Proposal options do not expose the estimate T&M override. It
                  was a dead control in this flow and is intentionally absent,
                  including for legacy options that still carry TM_OVERRIDE. */}
              <FinancialsSection
                discountValue={opt.discountType ? (opt.discountValue ?? 0) : 0}
                discountType={
                  opt.discountType === 'PERCENT'
                    ? '%'
                    : opt.discountType === 'FLAT'
                      ? '$'
                      : undefined
                }
                discountReason={opt.discountReason ?? ''}
                onDiscountValueChange={(discountValue) => onUpdate({ discountValue })}
                onDiscountTypeChange={(discountType) =>
                  onUpdate({
                    discountType:
                      discountType === '%' ? 'PERCENT' : discountType === '$' ? 'FLAT' : undefined,
                    discountValue: discountType == null ? undefined : (opt.discountValue ?? 0),
                    discountReason: discountType == null ? undefined : opt.discountReason,
                  })
                }
                onDiscountReasonChange={(discountReason) => onUpdate({ discountReason })}
                depositValue={opt.depositValue}
                depositType={opt.depositType}
                depositRequired={opt.depositRequired}
                onDepositValueChange={(depositValue) => onUpdate({ depositValue })}
                onDepositTypeChange={(depositType) => onUpdate({ depositType })}
                onDepositRequiredChange={(depositRequired) => onUpdate({ depositRequired })}
                subtotal={subtotal}
                discountAmount={discountAmount}
                depositAmount={depositAmount}
                grandTotal={total}
                // Parity with the estimate form: the proposal server refuses a
                // deposit above 100% / this option's total, so the option says so
                // inline rather than letting the builder and the PDF disagree.
                // Judged per OPTION, against its own deposit-eligible base.
                enforceDepositBound
                depositEligibleTotal={depositEligibleTotal}
                depositChanged={depositChanged}
                showTax={false}
                showSummary={false}
                // NOT-605 — surface the member savings line when the proposal's
                // customer is an active member and this option benefits.
                membershipDiscountPreview={optionPreview.membershipDiscountPreview}
                membershipInfo={membershipInfo}
              />
              {paymentScheduleControls}
              <DocumentCostPanel
                lineItems={lineItems}
                pricingMode={opt.pricingMode}
                subtotal={subtotal}
                taxTotal={0}
                grandTotal={total}
              />
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
